import { z } from 'zod';
import { DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS } from './sessionActivityPing';

const AccountTypeSessionConfigSchema = z
    .object({
        sessionExpirationTime: z.number().optional(),
        activeSessionExtensionTime: z.number().optional(),
        markActiveThreshold: z.number().optional(),
    })
    .passthrough();

export const AccountsConfigSchema = z
    .object({
        accountTypes: z.record(AccountTypeSessionConfigSchema),
    })
    .passthrough();

export type AccountsConfigType = z.infer<typeof AccountsConfigSchema>;

const AccountsConfigResultSchema = z.union([
    z.object({
        type: z.literal('ok'),
        data: AccountsConfigSchema,
    }),
    z.object({
        type: z.literal('error'),
        message: z.string(),
        status: z.number().optional(),
    }),
]);

const TrpcAccountsConfigResponseSchema = z.object({
    result: z.object({
        data: AccountsConfigResultSchema,
    }),
});

export type SessionActivityPingAccountType = 'customer' | 'anonymous';

export const parseInactivityThresholdMsFromAccountsConfig = (
    config: AccountsConfigType,
    accountType: string
): number => {
    const markActiveThreshold = config.accountTypes[accountType]?.markActiveThreshold;

    if (markActiveThreshold === undefined) {
        return DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;
    }
    return markActiveThreshold * 1000;
};

export const fetchSessionActivityInactivityThresholdMs = async (params: {
    jwt: string;
    accountType: SessionActivityPingAccountType;
}): Promise<number> => {
    const accountType = params.accountType;

    try {
        const response = await fetch('/trpc/accountsConfig.getAccountsConfig', {
            method: 'GET',
            headers: {
                Authorization: params.jwt,
            },
        });

        if (!response.ok) {
            console.warn(
                `fetchSessionActivityInactivityThresholdMs: unexpected status ${response.status}, using default`
            );
            return DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;
        }

        const json: unknown = await response.json();
        const parsed = TrpcAccountsConfigResponseSchema.safeParse(json);

        if (!parsed.success) {
            console.warn(
                'fetchSessionActivityInactivityThresholdMs: invalid trpc response, using default',
                parsed.error.message
            );
            return DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;
        }

        const result = parsed.data.result.data;

        if (result.type === 'error') {
            console.warn('fetchSessionActivityInactivityThresholdMs: trpc error, using default', result.message);
            return DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;
        }

        return parseInactivityThresholdMsFromAccountsConfig(result.data, accountType);
    } catch (error) {
        console.warn('fetchSessionActivityInactivityThresholdMs: fetch failed, using default', error);
        return DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;
    }
};
